import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DS_API } from "../constants";
import chatbotlogo from "../assets/images/logos/chatbotlogo2.png"
import { 
  MessageCircle, 
  Loader2, 
  Leaf, 
  X, 
  Send, 
  Mic, 
  Paperclip,
  Smile,
  Bot,
  User
} from "lucide-react";

export default function ChatbotUI({ open, setOpen }) {
  const [isMobile, setIsMobile] = useState(false);
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi there! I'm the SustainCred Assistant. How can I support your sustainability journey today?",
      sender: "bot",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Check screen size and adjust layout
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendToAPI = async (text) => {
    try {
      // Send data in the required format: { "question": "user's question" }
      const res = await fetch(`${DS_API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ question: text })
      });

      const data = await res.json();
      // Extract the answer from the response format: { question, answer }
      return data.answer || "Sorry, I couldn't process your request.";
    } catch (error) {
      console.error("API Error:", error);
      return "Sorry, I couldn't reach the server. Please try again.";
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userText = inputMessage;
    setInputMessage("");

    const newUserMessage = {
      id: messages.length + 1,
      text: userText,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setTyping(true);

    // API call with question format
    const apiReply = await sendToAPI(userText);

    const botMessage = {
      id: messages.length + 2,
      text: apiReply,
      sender: "bot",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);
    setTyping(false);
  };

  // Quick Action Buttons
  const handleQuickAction = async (action) => {
    const quickMessage = {
      id: messages.length + 1,
      text: action,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, quickMessage]);
    setTyping(true);

    // API call with quick action text in question format
    const apiReply = await sendToAPI(action);

    const botMessage = {
      id: messages.length + 2,
      text: apiReply,
      sender: "bot",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);
    setTyping(false);
  };

  const quickActions = [
    { label: "What is carbon credit?", icon: Leaf },
    { label: "How to reduce carbon footprint?", icon: MessageCircle },
    { label: "What is ESG reporting?", icon: Bot }
  ];

  // Mobile full-screen layout
  if (isMobile && open) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-white flex flex-col"
      >
        {/* Header */}
        <div className="w-full bg-gradient-to-r from-green-700 to-green-500 p-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 8, -6, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center"
            >
              <img src={chatbotlogo} alt="SustainCred Logo" className="w-[20px] h-[20px] object-contain" />
            </motion.div>
            <div className="leading-tight">
              <div className="text-white font-bold text-lg">SustainCred Assistant</div>
              <div className="text-white/90 text-sm">Always active</div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="text-white" size={24} />
          </button>
        </div>

        {/* Messages - Full conversation history preserved */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto bg-gradient-to-b from-green-50 to-white p-4"
        >
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`flex gap-3 max-w-[85%] ${message.sender === "user" ? "flex-row-reverse" : ""}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.sender === "user" 
                      ? "bg-blue-500" 
                      : "bg-green-600"
                  }`}>
                    {message.sender === "user" ? (
                      <User className="text-white" size={16} />
                    ) : (
                      <Bot className="text-white" size={16} />
                    )}
                  </div>
                  <div className={`p-3 rounded-2xl ${
                    message.sender === "user"
                      ? "bg-blue-500 text-white rounded-br-none"
                      : "bg-white text-gray-800 rounded-bl-none shadow-sm border border-green-100"
                  }`}>
                    <p className="text-sm leading-relaxed">{message.text}</p>
                    <p className={`text-xs mt-1 ${
                      message.sender === "user" ? "text-blue-100" : "text-gray-500"
                    }`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}

            {typing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="flex gap-3 max-w-[85%]">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="text-white" size={16} />
                  </div>
                  <div className="bg-white p-3 rounded-2xl rounded-bl-none shadow-sm border border-green-100">
                    <div className="flex items-center gap-2">
                      <Loader2 className="animate-spin text-green-600" size={14} />
                      <span className="text-xs text-gray-500">Assistant is typing...</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-3 bg-white border-t border-gray-200">
          <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleQuickAction(action.label)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-200 text-green-700 text-sm font-medium whitespace-nowrap hover:bg-green-100 transition-colors"
              >
                <action.icon size={16} />
                {action.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-200">
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
              <Paperclip className="text-gray-500" size={20} />
            </button>
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask me anything about sustainability..."
                className="w-full px-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100">
                <Smile className="text-gray-500" size={20} />
              </button>
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
              className="p-3 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={20} />
            </button>
            <button className="p-3 rounded-full hover:bg-gray-100 transition-colors">
              <Mic className="text-gray-500" size={20} />
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop popup layout
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="fixed right-6 bottom-10 z-50 w-96 h-[600px] bg-white rounded-2xl shadow-2xl border border-green-200 overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="w-full bg-gradient-to-r from-green-700 to-green-500 p-4 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
              <img
                src={chatbotlogo}
                alt="SustainCred Logo"
                className="w-[40px] h-[40px] object-contain"
              />
              <div className="leading-tight">
                <div className="text-white font-semibold">SustainCred Assistant</div>
                <div className="text-white/80 text-xs">Sustainability help & reporting</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md hover:bg-white/10 transition-colors"
            >
              <X className="text-white" size={18} />
            </button>
          </div>

          {/* Messages - Full conversation history preserved */}
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto bg-gradient-to-b from-green-50 to-white p-4"
          >
            <div className="space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`flex gap-2 max-w-[80%] ${message.sender === "user" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.sender === "user" 
                        ? "bg-blue-500" 
                        : "bg-green-600"
                    }`}>
                      {message.sender === "user" ? (
                        <User className="text-white" size={12} />
                      ) : (
                        <Bot className="text-white" size={12} />
                      )}
                    </div>
                    <div className={`p-3 rounded-xl ${
                      message.sender === "user"
                        ? "bg-blue-500 text-white rounded-br-none"
                        : "bg-white text-gray-800 rounded-bl-none shadow-sm border border-green-100"
                    }`}>
                      <p className="text-sm leading-relaxed">{message.text}</p>
                      <p className={`text-xs mt-1 ${
                        message.sender === "user" ? "text-blue-100" : "text-gray-400"
                      }`}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {typing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="flex gap-2 max-w-[80%]">
                    <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="text-white" size={12} />
                    </div>
                    <div className="bg-white p-3 rounded-xl rounded-bl-none shadow-sm border border-green-100">
                      <div className="flex items-center gap-2">
                        <Loader2 className="animate-spin text-green-600" size={14} />
                        <span className="text-xs text-gray-500">Assistant is typing...</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => handleQuickAction(action.label)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium whitespace-nowrap hover:bg-green-100 transition-colors"
                >
                  <action.icon size={14} />
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex items-center gap-2">
              <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <Paperclip className="text-gray-500" size={16} />
              </button>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Ask me anything about sustainability..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded hover:bg-gray-100">
                  <Smile className="text-gray-500" size={14} />
                </button>
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim()}
                className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}